/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */
"use client";

import {
  FeaturePageTemplate,
  type FeaturePageConfig,
} from "@/components/landing/feature-page-template";
import {
  Sparkles,
  Calculator,
  MessageSquare,
  Ruler,
  BookOpen,
  BarChart3,
  Receipt,
  Layers,
} from "lucide-react";

const config: FeaturePageConfig = {
  badge: "AI Built for Concrete Contractors",
  title: "AI for concrete contractors",
  titleHighlight: "from pour to paid.",
  description: `One platform where AI helps concrete contractors bid faster, match costs to real supplier rates, audit scope before proposals ship, and run projects from one database. Built for 3–25 person teams. Grounded in your real business data — not generic construction averages.`,

  featureGrid: {
    title: "Where AI shows up for concrete contractors",
    items: [
      {
        icon: Sparkles,
        title: "Yardage from measurements",
        description:
          "Draw slab, footing, and wall areas on the drawing; the system converts to cubic yards with waste factors applied from your cost database.",
      },
      {
        icon: Sparkles,
        title: "Rebar and mesh line items",
        description:
          "Track reinforcement separately from pour volume — every line matched to your cost database.",
      },
      {
        icon: Sparkles,
        title: "Formwork as structured data",
        description:
          "Linear feet of formwork captured per work area, matched to your labor and material costs.",
      },
      {
        icon: Sparkles,
        title: "Finishing labor",
        description:
          "Broom, trowel, stamp — different labor rates for different finishes.",
      },
      {
        icon: Sparkles,
        title: "Pump and delivery tracking",
        description: "Pump trucks and delivery fees as separate line items.",
      },
      {
        icon: Sparkles,
        title: "Hank for concrete",
        description:
          "'What's my average margin on driveway pours this year?' Real answer.",
      },
    ],
  },

  workflow: {
    title: "How it works for concrete contractors",
    description:
      "From first bid to final invoice — every step in one database, every AI decision reviewable.",
    steps: [
      {
        label: "Measure",
        title: "Draw slab areas on the plans",
        description:
          "Upload the drawings, calibrate the scale, and draw slab, footing, and wall measurements on the canvas. You're faster than any AI at reading a concrete plan. Cubic yards compute from your measurements with waste factors applied.",
        details: [],
      },
      {
        label: "Bid",
        title: "AI matches every line to your cost database",
        description:
          "Concrete, rebar, forms, finishing labor — each line matched against your cost database. Variance factors adjust for complex pours and hard-access sites. Hank audits the estimate for missing items before it ships.",
        details: [],
      },
      {
        label: "Run",
        title: "Project tracking through cure",
        description:
          "Accepted bid becomes a project with pour scheduling, delivery coordination, and inspection tracking. Budget vs. actual on every pour.",
        details: [],
      },
    ],
  },

  capabilities: [
    {
      icon: Sparkles,
      title: "Yardage math that doesn't lie",
      description:
        "Concrete jobs fail when yardage is off. Draw your slab, footing, and wall areas on the drawing and the system computes cubic yards from your measurements with waste factors automatically applied for slump and form loss. No more 'short load' surprises on pour day.",
      bullets: [
        "You draw, system computes yardage",
        "Automatic cubic yard calculation from your measurements",
        "Waste factors per pour type",
        "Slump and form loss accounted for",
      ],
    },
    {
      icon: Sparkles,
      title: "Labor variance for finish type",
      description:
        "A broom-finished driveway and a stamped patio are different labor rates. Variance factors adjust production rates per finish so your finishing labor estimates actually match reality.",
      bullets: [
        "Per-finish labor variance",
        "Separate rates for broom, trowel, stamp",
        "Weather-adjusted production rates",
        "Crew-size dependent rates",
      ],
    },
    {
      icon: Sparkles,
      title: "Rebar, forms, and pump trucks as first-class line items",
      description:
        "Most estimating tools bundle rebar and formwork into the concrete line. Contractor Co-Pilot tracks them separately so you can bid a job where the client is supplying rebar or providing their own formwork.",
      bullets: [
        "Rebar tracked separately from concrete",
        "Formwork by linear foot",
        "Pump truck and delivery fees as line items",
        "Bulk vs. bagged concrete tracking",
      ],
    },
    {
      icon: Sparkles,
      title: "AI audits yardage before you lock it in",
      description:
        "Hank checks your yardage against historical jobs and flags anything that looks off. A 12-yard pour on a 600 SF slab might be a typo — Hank catches it before you order the truck.",
      bullets: [
        "Historical yardage comparison",
        "Slab vs. footing ratio checks",
        "Anomaly detection on volume",
        "Plain-English audit summaries",
      ],
    },
  ],

  relatedFeatures: [
    { label: "Concrete Solutions", href: "/solutions/concrete", icon: Ruler },
    {
      label: "Concrete Calculator",
      href: "/tools/concrete-calculator",
      icon: Calculator,
    },
    {
      label: "How to Estimate Concrete Work",
      href: "/blog/how-to-estimate-concrete-work",
      icon: BookOpen,
    },
    { label: "CC vs PlanSwift", href: "/compare/planswift", icon: BarChart3 },
    {
      label: "Construction Estimating",
      href: "/features/estimates",
      icon: Receipt,
    },
    { label: "Blueprint Takeoffs", href: "/features/takeoffs", icon: Layers },
    {
      label: "AI Assistant (Hank)",
      href: "/features/ai-assistant",
      icon: MessageSquare,
    },
    { label: "All Features", href: "/features", icon: Sparkles },
  ],
};

export function AiForConcreteContractorsPageClient() {
  return <FeaturePageTemplate config={config} />;
}
